import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Facebook,
  FileText,
  Instagram,
  Leaf,
  Linkedin,
  Mail,
  Map,
  MapPin,
  Phone,
  Youtube,
} from "lucide-react";
import { useToast } from "../hooks/use-toast";

const SITEMAP_LINKS = [
  { label: "Sitemap page 1", to: "/sitemap" },
  { label: "Sitemap page 2", to: "/sitemap/2" },
  { label: "Sitemap page 3", to: "/sitemap/3" },
  { label: "Sitemap page 4", to: "/sitemap/4" },
  { label: "Sitemap page 5", to: "/sitemap/5" },
  { label: "Sitemap page 6", to: "/sitemap/6" },
  { label: "Sitemap page 7", to: "/sitemap/7" },
  { label: "Sitemap page 8", to: "/sitemap/8" },
];

const COLS = [
  {
    title: "Pages",
    links: [
      { label: "Home", to: "/" },
      { label: "Volunteer in Bali", to: "/projects/volunteer-in-bali" },
      { label: "Blog: Cape Verde", to: "/blog/find-out-more-about-cape-verde" },
      { label: "HTML Sitemap", to: "/sitemap" },
    ],
  },
  {
    title: "HTML Sitemap 1-4",
    links: [
      { label: "Sitemap page 1", to: "/sitemap" },
      { label: "Sitemap page 2", to: "/sitemap/2" },
      { label: "Sitemap page 3", to: "/sitemap/3" },
      { label: "Sitemap page 4", to: "/sitemap/4" },
    ],
  },
  {
    title: "HTML Sitemap 5-8",
    links: [
      { label: "Sitemap page 5", to: "/sitemap/5" },
      { label: "Sitemap page 6", to: "/sitemap/6" },
      { label: "Sitemap page 7", to: "/sitemap/7" },
      { label: "Sitemap page 8", to: "/sitemap/8" },
    ],
  },
];

export default function Footer() {
  const [email, setEmail] = useState("");
  const { toast } = useToast();

  const subscribe = (e) => {
    e.preventDefault();
    if (!email.includes("@")) {
      toast({ title: "Hmm, that email looks off", description: "Please enter a valid email address." });
      return;
    }
    const list = JSON.parse(localStorage.getItem("ivi_subscribers") || "[]");
    localStorage.setItem("ivi_subscribers", JSON.stringify([...list, email]));
    toast({
      title: "You're on the list!",
      description: `We'll send the monthly IVI newsletter to ${email}.`,
    });
    setEmail("");
  };

  return (
    <footer className="bg-ivi-slate text-white/70">
      <div className="mx-auto max-w-[1400px] px-5 py-20 lg:px-8">
        <div className="grid gap-14 lg:grid-cols-[1.3fr_1fr_1fr_1fr]">
          <div>
            <div className="flex items-center gap-3">
              <span className="grid h-11 w-11 place-items-center rounded-full bg-ivi-green text-white">
                <Leaf className="h-5 w-5" strokeWidth={2.2} />
              </span>
              <span className="font-heading text-[15px] font-extrabold leading-tight text-white">
                Involvement Volunteers
                <span className="block text-[11px] font-semibold uppercase tracking-[0.18em] text-ivi-lime">
                  International
                </span>
              </span>
            </div>
            <p className="mt-6 max-w-sm text-[15px] leading-relaxed">
              A registered non-profit charity established in 1989. Together with partners worldwide we
              educate, fight poverty, improve health and empower change through sustainable volunteer
              work.
            </p>
            <div className="mt-7 space-y-3 text-[14px]">
              <p className="flex items-center gap-3">
                <MapPin className="h-4 w-4 text-ivi-lime" /> Melbourne, Victoria, Australia
              </p>
              <p className="flex items-center gap-3">
                <Phone className="h-4 w-4 text-ivi-lime" /> +61 3 9646 9392
              </p>
              <p className="flex items-center gap-3">
                <Mail className="h-4 w-4 text-ivi-lime" /> info@volunteering.org.au
              </p>
            </div>
            <div className="mt-7 flex gap-2">
              {[Facebook, Instagram, Youtube, Linkedin].map((Icon, i) => (
                <a
                  key={i}
                  href="#social"
                  aria-label="Social"
                  className="grid h-10 w-10 place-items-center rounded-full bg-white/10 text-white transition-colors duration-200 hover:bg-ivi-green"
                >
                  <Icon className="h-[18px] w-[18px]" />
                </a>
              ))}
            </div>
          </div>

          {COLS.map((col) => (
            <div key={col.title}>
              <h4 className="font-heading text-[13px] font-bold uppercase tracking-[0.16em] text-white">
                {col.title}
              </h4>
              <ul className="mt-5 space-y-3 text-[15px]">
                {col.links.map((l) => (
                  <li key={l.label}>
                    <Link to={l.to} className="transition-colors duration-200 hover:text-ivi-lime">
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-16 rounded-3xl bg-white/[0.06] p-8 ring-1 ring-white/10 md:p-10">
          <div className="flex flex-wrap items-center gap-3">
            <span className="grid h-10 w-10 place-items-center rounded-xl bg-ivi-green text-white">
              <Map className="h-5 w-5" />
            </span>
            <h4 className="font-heading text-[15px] font-bold uppercase tracking-[0.16em] text-white">
              HTML sitemap &amp; articles
            </h4>
            <span className="rounded-full bg-white/10 px-3 py-1 text-[12px] font-semibold text-ivi-lime">
              1,300 pages across 8 sitemap pages
            </span>
          </div>
          <div className="mt-6 flex flex-wrap gap-2.5">
            {SITEMAP_LINKS.map((s) => (
              <Link
                key={s.to}
                to={s.to}
                className="rounded-full bg-white/10 px-4 py-2.5 font-heading text-[13px] font-bold text-white transition-[background-color,transform] duration-200 hover:-translate-y-0.5 hover:bg-ivi-green"
              >
                {s.label}
              </Link>
            ))}
            <Link
              to="/blog/find-out-more-about-cape-verde"
              className="inline-flex items-center gap-2 rounded-full bg-ivi-lime px-4 py-2.5 font-heading text-[13px] font-extrabold text-ivi-slate transition-transform duration-200 hover:-translate-y-0.5"
            >
              <FileText className="h-3.5 w-3.5" /> Blog: Find out more about Cape Verde!
            </Link>
          </div>
        </div>

        <div className="mt-8 rounded-3xl bg-white/[0.06] p-8 ring-1 ring-white/10 md:p-10">
          <div className="grid gap-8 md:grid-cols-[1.1fr_1fr] md:items-center">
            <div>
              <h3 className="font-heading text-2xl font-extrabold text-white">
                Subscribe to our monthly newsletter
              </h3>
              <p className="mt-2 text-[15px]">
                Exclusive offers, new programs, travel inspiration and real vollie stories.
              </p>
            </div>
            <form onSubmit={subscribe} className="flex flex-col gap-3 sm:flex-row">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Your email address"
                className="h-12 flex-1 rounded-full border border-white/15 bg-white/10 px-5 text-[15px] text-white placeholder:text-white/50 outline-none transition-[border-color,box-shadow] duration-200 focus:border-ivi-lime focus:ring-2 focus:ring-ivi-lime/40"
              />
              <button
                type="submit"
                className="inline-flex h-12 items-center justify-center gap-2 rounded-full bg-ivi-green px-6 font-bold text-white transition-[background-color,transform] duration-200 hover:-translate-y-0.5 hover:bg-ivi-greenDark"
              >
                Subscribe <ArrowRight className="h-4 w-4" />
              </button>
            </form>
          </div>
        </div>

        <div className="mt-12 flex flex-col gap-3 border-t border-white/10 pt-8 text-[13px] md:flex-row md:items-center md:justify-between">
          <p>© {new Date().getFullYear()} Involvement Volunteers International. ABN 63 006 589 576.</p>
          <p className="flex flex-wrap gap-x-6 gap-y-2">
            <Link to="/sitemap" className="hover:text-ivi-lime">Sitemap</Link>
            <a href="#privacy" className="hover:text-ivi-lime">Privacy Policy</a>
            <a href="#terms" className="hover:text-ivi-lime">Terms & Conditions</a>
            <a href="#safety" className="hover:text-ivi-lime">Child Protection Policy</a>
          </p>
        </div>
      </div>
    </footer>
  );
}
